import React, { useState, useRef, useEffect } from "react";
import {
    Box,
    Button,
    Container,
    FormControl,
    FormLabel,
    Heading,
    Image,
    Input,
    Text,
    VStack,
    useToast,
    Center,
    Alert,
    AlertIcon,
    AlertDescription,
} from "@chakra-ui/react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle, Mail } from "lucide-react";
import { supabase } from "./utils/supabaseClient";
import { getAppOrigin } from "./utils/url";

const ForgotPassword = () => {
    const [email, setEmail] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [state, setState] = useState("form"); // "form" | "sent"
    const [logoURL, setLogoURL] = useState(null);
    const emailInputRef = useRef(null);
    const toast = useToast();

    useEffect(() => {
        const getOrganisationLogo = async () => {
            const { data } = await supabase
                .from("organization")
                .select("logo_url")
                .single();
            if (data) setLogoURL(data.logo_url);
        };
        getOrganisationLogo();
    }, []);

    useEffect(() => {
        if (state === "form") {
            const timer = setTimeout(() => {
                if (emailInputRef.current) emailInputRef.current.focus();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [state]);

    const sendResetEmail = async () => {
        setIsLoading(true);
        const redirectTo = `${getAppOrigin()}/reset-password`;
        try {
            const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
                redirectTo,
            });
            if (error) {
                if (error.message?.toLowerCase().includes("rate limit")) {
                    toast({
                        title: "Too many requests",
                        description: "Email sending is temporarily limited. Please wait a few minutes and try again.",
                        status: "warning",
                        duration: 6000,
                        isClosable: true,
                    });
                } else {
                    // For any other error, still show sent — never reveal whether the email exists
                    setState("sent");
                }
            } else {
                setState("sent");
            }
        } catch (err) {
            console.error("[ForgotPassword] Unexpected exception:", err);
            setState("sent");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        sendResetEmail();
    };

    const handleResend = async () => {
        setIsLoading(true);
        const redirectTo = `${getAppOrigin()}/reset-password`;
        try {
            const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
                redirectTo,
            });
            if (error?.message?.toLowerCase().includes("rate limit")) {
                toast({
                    title: "Too many requests",
                    description: "Email sending is temporarily limited. Please wait a few minutes and try again.",
                    status: "warning",
                    duration: 6000,
                    isClosable: true,
                });
                setIsLoading(false);
                return;
            }
            toast({
                title: "Reset link sent",
                description: "Another reset link has been sent to your email.",
                status: "success",
                duration: 4000,
                isClosable: true,
            });
        } catch {
            toast({
                title: "Something went wrong",
                description: "Please try again in a moment.",
                status: "error",
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box
            h="100vh"
            w="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
            px={{ base: 4, md: 0 }}
            bg={{ base: "gray.50", md: "white" }}
            overflow="hidden"
            position="relative"
        >
            <Container maxW="container.sm" w="100%" px={{ base: 0, md: 4 }} mt={{ base: -8, md: -12 }}>
                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                >
                    <Box
                        bg="white"
                        p={{ base: 6, md: 8 }}
                        borderRadius="lg"
                        boxShadow={{ base: "sm", md: "md" }}
                        textAlign="center"
                        w="100%"
                    >
                        <VStack spacing={5} align="center">
                            {/* Logo */}
                            <motion.div
                                initial={{ y: -50 }}
                                animate={{ y: 0 }}
                                transition={{ delay: 0.2, duration: 0.8, ease: "easeOut" }}
                            >
                                {logoURL && (
                                    <Image
                                        src={logoURL}
                                        alt="Logo"
                                        boxSize={{ base: "120px", md: "160px" }}
                                        objectFit="contain"
                                        mb={0}
                                    />
                                )}
                            </motion.div>

                            {state === "form" ? (
                                <FormState
                                    email={email}
                                    setEmail={setEmail}
                                    isLoading={isLoading}
                                    onSubmit={handleSubmit}
                                    emailInputRef={emailInputRef}
                                />
                            ) : (
                                <SentState
                                    email={email}
                                    isLoading={isLoading}
                                    onResend={handleResend}
                                    onUseDifferentEmail={() => {
                                        setEmail("");
                                        setState("form");
                                    }}
                                />
                            )}
                        </VStack>
                    </Box>

                    {/* Back to login */}
                    <Center mt={6}>
                        <Link to="/login" style={{ textDecoration: "none" }}>
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                color="gray.500"
                                fontSize="sm"
                                _hover={{ color: "gray.700" }}
                                cursor="pointer"
                            >
                                <ArrowLeft size={15} />
                                <Text fontSize="sm" color="gray.500">Back to login</Text>
                            </Box>
                        </Link>
                    </Center>
                </motion.div>
            </Container>
        </Box>
    );
};

const FormState = ({ email, setEmail, isLoading, onSubmit, emailInputRef }) => (
    <>
        <Heading size="sm" pb={1}>
            Forgot your password?
        </Heading>
        <Text fontSize="sm" color="gray.500" textAlign="center" maxW="320px">
            Enter your email address and we'll send you a link to reset your password.
        </Text>

        <Box as="form" onSubmit={onSubmit} w="100%" textAlign="left">
            <VStack spacing={4} align="stretch">
                <FormControl>
                    <FormLabel fontSize={{ base: "sm", md: "md" }} mb={2}>
                        Email
                    </FormLabel>
                    <Input
                        ref={emailInputRef}
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        bg="white"
                        size={{ base: "md", md: "lg" }}
                        fontSize={{ base: "16px", md: "md" }}
                        minH={{ base: "48px", md: "40px" }}
                        autoComplete="email"
                        autoCapitalize="none"
                        autoCorrect="off"
                        inputMode="email"
                    />
                </FormControl>

                <Button
                    type="submit"
                    mt={2}
                    width="full"
                    height={{ base: "48px", md: "44px" }}
                    color="white"
                    bg="gray.800"
                    _hover={{ bg: "gray.700" }}
                    _active={{ bg: "gray.900" }}
                    isLoading={isLoading}
                    isDisabled={!email.trim() || isLoading}
                    fontSize={{ base: "md", md: "lg" }}
                    fontWeight="semibold"
                    loadingText="Sending…"
                >
                    Send reset link
                </Button>
            </VStack>
        </Box>
    </>
);

const SentState = ({ email, isLoading, onResend, onUseDifferentEmail }) => (
    <>
        {/* Success icon */}
        <Center
            w="64px"
            h="64px"
            borderRadius="full"
            bg="green.50"
            border="2px solid"
            borderColor="green.200"
        >
            <CheckCircle size={30} color="#38A169" />
        </Center>

        <Heading size="sm">Check your email</Heading>

        <Text fontSize="sm" color="gray.600" textAlign="center" maxW="340px">
            We sent a password reset link to{" "}
            <Text as="span" fontWeight="semibold" color="gray.800">
                {email}
            </Text>
            . The link expires in 1 hour.
        </Text>

        {/* Spam tip */}
        <Alert status="warning" borderRadius="md" fontSize="sm" textAlign="left">
            <AlertIcon as={Mail} boxSize={4} />
            <AlertDescription fontSize="xs" lineHeight="1.5">
                Don't see it? Check your spam or junk folder. If you still can't find it, try resending below.
            </AlertDescription>
        </Alert>

        <Button
            variant="outline"
            width="full"
            height={{ base: "44px", md: "40px" }}
            onClick={onResend}
            isLoading={isLoading}
            loadingText="Resending…"
            fontSize={{ base: "sm", md: "md" }}
            fontWeight="semibold"
            borderColor="gray.300"
            color="gray.700"
            _hover={{ bg: "gray.50" }}
        >
            Resend reset link
        </Button>

        <Button
            variant="ghost"
            size="sm"
            color="gray.500"
            fontWeight="normal"
            _hover={{ color: "gray.700", bg: "transparent", textDecoration: "underline" }}
            onClick={onUseDifferentEmail}
        >
            Use a different email address
        </Button>
    </>
);

export default ForgotPassword;
